import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getTangoDashboard, refreshTango } from '../api';
import toast from '../toast';
import { MODULE_ICONS } from '../components/Sidebar';
import { NivelBadge } from '../components/UI';
import ProyeccionWidget from './cartera/proyeccion/ProyeccionWidget';
import { Trophy, Users, RefreshCw, ChevronLeft, ChevronRight } from 'lucide-react';

const MESES_ES = ['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'];

function getMesParam(offset) {
  const d = new Date();
  d.setDate(1);
  d.setMonth(d.getMonth() + offset);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

function getMesLabel(mesParam) {
  const [year, month] = mesParam.split('-').map(Number);
  return `${MESES_ES[month - 1]} ${year}`;
}

// ── Module Card ───────────────────────────────────────────────────────────────
function ModuleCard({ modulo, onClick }) {
  const [hovered, setHovered] = useState(false);
  const isActive = modulo.active !== false;
  const Icon = MODULE_ICONS[modulo.id];

  return (
    <div
      onClick={isActive ? onClick : undefined}
      onMouseEnter={() => isActive && setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        background: 'var(--surface)',
        border: `1px solid ${hovered ? 'var(--accent)' : 'var(--border)'}`,
        borderRadius: 'var(--radius)',
        padding: 20,
        cursor: isActive ? 'pointer' : 'default',
        transform: hovered ? 'translateY(-2px)' : 'none',
        transition: 'border-color .15s, transform .15s',
        opacity: isActive ? 1 : 0.55,
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
      }}
    >
      {!isActive && (
        <div style={{
          position: 'absolute', top: 10, right: 10,
          background: 'var(--surface-3)', color: 'var(--text-3)',
          fontSize: 10, fontWeight: 700, padding: '3px 8px',
          borderRadius: 100, textTransform: 'uppercase', letterSpacing: '.06em',
        }}>
          Próximamente
        </div>
      )}

      {/* Ícono en círculo accent-dim */}
      <div style={{
        width: 40, height: 40, borderRadius: 10,
        background: hovered ? 'var(--accent)' : 'var(--accent-dim)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        transition: 'background .15s, transform .15s',
        transform: hovered ? 'scale(1.08)' : 'none',
      }}>
        {Icon
          ? <Icon size={20} strokeWidth={1.75} color={hovered ? 'white' : 'var(--accent)'} />
          : <span style={{ fontSize: 20 }}>{modulo.icon}</span>
        }
      </div>

      <div>
        <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 2 }}>{modulo.label}</div>
        <div style={{ fontSize: 12, color: isActive ? 'var(--text-2)' : 'var(--text-3)' }}>
          {isActive ? 'Ir al módulo →' : 'En desarrollo'}
        </div>
      </div>
    </div>
  );
}

// ── Podio de vendedores ───────────────────────────────────────────────────────
function PodioVendedores({ vendedores }) {
  if (!vendedores || vendedores.length === 0) {
    return <div style={{ color: 'var(--text-3)', fontSize: 13 }}>Sin datos para el período.</div>;
  }

  const fmt = (n) => n?.toLocaleString('es-AR', { minimumFractionDigits: 0, maximumFractionDigits: 0 });

  const podio = vendedores.slice(0, 3);
  const resto = vendedores.slice(3);

  const ordenPodio = [podio[1] || null, podio[0] || null, podio[2] || null];
  const alturas    = ['80px', '110px', '60px'];
  const emojis     = ['🥈', '🥇', '🥉'];
  const posLabels  = [2, 1, 3];
  const podioColors = ['#8fa3b8', 'var(--accent)', '#a0714f'];

  return (
    <div>
      {/* Podio visual */}
      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'center', gap: 8, marginBottom: 20 }}>
        {ordenPodio.map((v, idx) => {
          if (!v) return <div key={idx} style={{ flex: 1 }} />;
          return (
            <div key={v.nombre} style={{ flex: 1, textAlign: 'center' }}>
              <div style={{ fontSize: 11, color: 'var(--text-3)', marginBottom: 4 }}>{emojis[idx]}</div>
              {v.is_ecommerce ? (
                <div style={{ width: 40, height: 40, borderRadius: '50%', background: 'var(--surface-3)', border: '2px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, margin: '0 auto 6px' }}>🛒</div>
              ) : v.picture_url ? (
                <img src={v.picture_url} alt={v.nombre} style={{ width: 40, height: 40, borderRadius: '50%', objectFit: 'cover', margin: '0 auto 6px', display: 'block', border: `2px solid ${podioColors[idx]}` }} />
              ) : null}
              <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{v.nombre}</div>
              <div style={{ fontSize: 11, color: 'var(--text-2)', marginBottom: 6, fontVariantNumeric: 'tabular-nums' }}>
                USD {fmt(v.total_usd)}
              </div>
              <div style={{ height: alturas[idx], background: podioColors[idx], borderRadius: '6px 6px 0 0', opacity: 0.85 }} />
              <div style={{ background: 'var(--surface-2)', padding: '3px 0', fontSize: 11, fontWeight: 700, color: 'var(--text-3)', borderRadius: '0 0 4px 4px' }}>#{posLabels[idx]}</div>
            </div>
          );
        })}
      </div>

      {/* Tabla posiciones 4+ */}
      {resto.length > 0 && (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ background: 'var(--surface-2)', borderBottom: '1px solid var(--border)' }}>
              <th style={{ padding: '8px 12px', textAlign: 'left', color: 'var(--text-3)', fontWeight: 500, fontSize: 11, textTransform: 'uppercase', width: 36 }}>#</th>
              <th style={{ padding: '8px 12px', textAlign: 'left', color: 'var(--text-3)', fontWeight: 500, fontSize: 11, textTransform: 'uppercase' }}>Vendedor</th>
              <th style={{ padding: '8px 12px', textAlign: 'right', color: 'var(--text-3)', fontWeight: 500, fontSize: 11, textTransform: 'uppercase' }}>Facturación</th>
            </tr>
          </thead>
          <tbody>
            {resto.map(v => (
              <tr key={v.nombre} style={{ borderBottom: '1px solid var(--border)' }}>
                <td style={{ padding: '10px 12px', color: 'var(--text-3)', fontVariantNumeric: 'tabular-nums', fontSize: 12 }}>
                  {String(v.posicion).padStart(2, '0')}
                </td>
                <td style={{ padding: '10px 12px' }}>{v.nombre}</td>
                <td style={{ padding: '10px 12px', textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: 'var(--text-2)' }}>
                  USD {fmt(v.total_usd)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

// ── Top Clientes ──────────────────────────────────────────────────────────────
function TopClientesTable({ clientes }) {
  const [busqueda, setBusqueda] = useState('');
  const [sortMode, setSortMode] = useState('posicion');

  if (!clientes || clientes.length === 0) {
    return <div style={{ color: 'var(--text-3)', fontSize: 13 }}>Sin datos del último año.</div>;
  }

  const clientesFiltrados = busqueda.trim()
    ? clientes.filter(c =>
        (c.razon_social || '').toLowerCase().includes(busqueda.toLowerCase()) ||
        (c.nombre_comercial || '').toLowerCase().includes(busqueda.toLowerCase())
      )
    : clientes;

  const clientesOrdenados = sortMode === 'nombre'
    ? [...clientesFiltrados].sort((a, b) =>
        (a.nombre_comercial || a.razon_social || '').localeCompare(
          b.nombre_comercial || b.razon_social || '', 'es-AR'))
    : clientesFiltrados;

  return (
    <div style={{ overflowX: 'auto', overflowY: 'auto', maxHeight: 380 }}>
      <div style={{ display: 'flex', gap: 8, marginBottom: 12, alignItems: 'center' }}>
        <input
          type="text"
          placeholder="Buscar cliente…"
          value={busqueda}
          onChange={e => setBusqueda(e.target.value)}
          style={{
            flex: 1, padding: '7px 12px', borderRadius: 6,
            border: '1px solid var(--border)', background: 'var(--surface-2)',
            color: 'var(--text)', fontSize: 13, outline: 'none', boxSizing: 'border-box',
          }}
        />
        <button
          onClick={() => setSortMode(m => m === 'posicion' ? 'nombre' : 'posicion')}
          style={{
            fontSize: 11, padding: '7px 10px', borderRadius: 6,
            border: '1px solid var(--border)',
            background: sortMode === 'nombre' ? 'var(--accent)' : 'var(--surface-2)',
            color: sortMode === 'nombre' ? 'white' : 'var(--text-2)',
            cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0,
          }}
        >
          {sortMode === 'posicion' ? 'A–Z' : 'Por posición'}
        </button>
      </div>

      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, minWidth: 360 }}>
        <thead>
          <tr style={{ background: 'var(--surface-2)', borderBottom: '1px solid var(--border)' }}>
            <th style={{ padding: '8px 12px', textAlign: 'left', color: 'var(--text-3)', fontWeight: 500, fontSize: 11, textTransform: 'uppercase', width: 32 }}>#</th>
            <th style={{ padding: '8px 12px', textAlign: 'left', color: 'var(--text-3)', fontWeight: 500, fontSize: 11, textTransform: 'uppercase' }}>Cliente</th>
            <th style={{ padding: '8px 12px', textAlign: 'center', color: 'var(--text-3)', fontWeight: 500, fontSize: 11, textTransform: 'uppercase', width: 52 }}>Nivel</th>
            <th style={{ padding: '8px 12px', textAlign: 'left', color: 'var(--text-3)', fontWeight: 500, fontSize: 11, textTransform: 'uppercase' }}>Vendedor</th>
          </tr>
        </thead>
        <tbody>
          {clientesOrdenados.length === 0 && busqueda.trim() ? (
            <tr><td colSpan={4} style={{ padding: '20px 12px', textAlign: 'center', color: 'var(--text-3)', fontSize: 13 }}>
              Sin resultados para "{busqueda}"
            </td></tr>
          ) : (
            clientesOrdenados.map(c => (
              <tr key={c.cod_cliente}
                style={{ borderBottom: '1px solid var(--border)', transition: 'background .1s' }}
                onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.02)'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
              >
                <td style={{ padding: '10px 12px', color: 'var(--text-3)', fontVariantNumeric: 'tabular-nums', fontSize: 12 }}>{c.posicion}</td>
                <td style={{ padding: '10px 12px', fontWeight: 500 }}>
                  <div>{c.nombre_comercial || c.razon_social}</div>
                  {c.nombre_comercial && c.razon_social !== c.nombre_comercial && (
                    <div style={{ fontSize: 11, color: 'var(--text-3)' }}>{c.razon_social}</div>
                  )}
                </td>
                <td style={{ padding: '10px 12px', textAlign: 'center' }}>
                  <NivelBadge nivel={c.nivel} vacio={<span style={{ color: 'var(--text-3)', fontSize: 12 }}>—</span>} />
                </td>
                <td style={{ padding: '10px 12px', color: 'var(--text-2)', fontSize: 12 }}>
                  {c.vendedor_principal || '—'}
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
      <div style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 10, textAlign: 'right' }}>
        Estos clientes concentran el 50% de la facturación del último año (excluye Ecommerce).
      </div>
    </div>
  );
}

// ── Widget Tango ──────────────────────────────────────────────────────────────
function TangoWidget({ role }) {
  const [data, setData]               = useState(null);
  const [loading, setLoading]         = useState(true);
  const [refreshing, setRefreshing]   = useState(false);
  const [periodoView, setPeriodoView] = useState('mensual');
  const [mesOffset, setMesOffset]     = useState(0);

  const load = useCallback(() => {
    setLoading(true);
    const params = periodoView === 'anual'
      ? { periodo: 'anual' }
      : { periodo: 'mensual', mes: getMesParam(mesOffset) };
    getTangoDashboard(params)
      .then(r => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [periodoView, mesOffset]);

  useEffect(() => { load(); }, [load]);

  const handleRefresh = () => {
    setRefreshing(true);
    refreshTango()
      .then(() => { load(); toast.success('Datos actualizados desde Tango.'); })
      .catch(() => toast.error('No se pudo actualizar.'))
      .finally(() => setRefreshing(false));
  };

  const handleTogglePeriodo = (p) => {
    setPeriodoView(p);
    if (p === 'mensual') setMesOffset(0);
  };

  const fmtDate = (iso) => {
    if (!iso) return '';
    const d = new Date(iso.endsWith('Z') ? iso : iso + 'Z');
    return d.toLocaleString('es-AR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
  };

  const canRefresh = role === 'admin' || role === 'dev';

  const btnNavStyle = (disabled) => ({
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    width: 28, height: 28, borderRadius: 6, border: '1px solid var(--border)',
    background: 'var(--surface-2)', color: disabled ? 'var(--text-3)' : 'var(--text-2)',
    cursor: disabled ? 'default' : 'pointer', padding: 0, flexShrink: 0,
  });

  if (loading && !data) return <div style={{ color: 'var(--text-3)', fontSize: 13, padding: 20 }}>Consultando Tango…</div>;
  if (!data)            return <div style={{ color: 'var(--text-3)', fontSize: 13, padding: 20 }}>No se pudieron cargar los datos de Tango.</div>;

  const mesSeleccionado = getMesParam(mesOffset);
  const esMesActual = mesOffset === 0;

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '7fr 5fr', gap: 16, opacity: loading ? 0.55 : 1, pointerEvents: loading ? 'none' : 'auto', transition: 'opacity .2s' }} className="tango-grid">
      {/* Podio vendedores */}
      <div className="card" style={{ minWidth: 0 }}>
        {/* Fila título + refresh */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Trophy size={16} strokeWidth={1.75} color="var(--amber)" />
            <span style={{ fontSize: 15, fontWeight: 600 }}>Podio de Vendedores</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {data.refreshed_at && (
              <span style={{ fontSize: 11, color: 'var(--text-3)' }}>
                {data.is_refreshing ? 'Actualizando…' : `Act. ${fmtDate(data.refreshed_at)}`}
              </span>
            )}
            {canRefresh && (
              <button onClick={handleRefresh} disabled={refreshing || loading}
                title="Forzar actualización desde Tango"
                style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, padding: '4px 8px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--surface-2)', color: 'var(--text-2)', cursor: 'pointer' }}>
                <RefreshCw size={12} strokeWidth={2} style={{ animation: refreshing ? 'spin .7s linear infinite' : 'none' }} />
                {refreshing ? 'Actualizando' : 'Actualizar'}
              </button>
            )}
          </div>
        </div>

        {/* Fila controles: toggle + navegación mes */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14, flexWrap: 'wrap', gap: 8 }}>
          {/* Toggle Mensual / Anual */}
          <div style={{ display: 'flex', background: 'var(--surface-2)', borderRadius: 8, padding: 2, gap: 2 }}>
            {['mensual', 'anual'].map(p => (
              <button key={p} onClick={() => handleTogglePeriodo(p)}
                style={{
                  padding: '4px 14px', borderRadius: 6, border: 'none', fontSize: 12, fontWeight: 500,
                  background: periodoView === p ? 'var(--accent)' : 'transparent',
                  color: periodoView === p ? 'white' : 'var(--text-2)',
                  cursor: 'pointer', transition: 'background .15s, color .15s',
                }}>
                {p === 'mensual' ? 'Mensual' : 'Anual'}
              </button>
            ))}
          </div>

          {/* Navegación mensual */}
          {periodoView === 'mensual' && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <button onClick={() => setMesOffset(o => o - 1)} style={btnNavStyle(false)}>
                <ChevronLeft size={14} strokeWidth={2} />
              </button>
              <span style={{ fontSize: 13, fontWeight: 500, minWidth: 110, textAlign: 'center', color: 'var(--text)' }}>
                {getMesLabel(mesSeleccionado)}
              </span>
              <button onClick={() => setMesOffset(o => Math.min(o + 1, 0))} disabled={esMesActual} style={btnNavStyle(esMesActual)}>
                <ChevronRight size={14} strokeWidth={2} />
              </button>
            </div>
          )}

          {/* Año en vista anual */}
          {periodoView === 'anual' && (
            <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-2)' }}>
              {new Date().getFullYear()} — acumulado al día de hoy
            </span>
          )}
        </div>

        {/* Botón volver al mes actual */}
        {periodoView === 'mensual' && !esMesActual && (
          <div style={{ marginBottom: 12, textAlign: 'center' }}>
            <button onClick={() => setMesOffset(0)}
              style={{
                fontSize: 12, padding: '5px 14px', borderRadius: 20,
                border: '1px solid var(--accent)', background: 'transparent',
                color: 'var(--accent)', cursor: 'pointer', fontWeight: 500,
              }}>
              Volver al mes actual
            </button>
          </div>
        )}

        <PodioVendedores vendedores={data.podio_vendedores} />
      </div>

      {/* Top clientes */}
      <div className="card" style={{ minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
          <Users size={16} strokeWidth={1.75} color="var(--blue)" />
          <span style={{ fontSize: 15, fontWeight: 600 }}>Top Clientes</span>
        </div>
        <TopClientesTable clientes={data.top_clientes} />
      </div>
    </div>
  );
}

// ── Dashboard principal ───────────────────────────────────────────────────────
export default function Dashboard() {
  const { user, effectiveRole: role, modules } = useAuth();
  const navigate = useNavigate();

  const showModules = role !== 'administracion';
  const visibleModules = showModules
    ? modules.filter(m => m.roles.includes(role))
    : [];

  return (
    <div className="page">
      {/* Saludo */}
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, marginBottom: 4 }}>
          Hola, {user?.name?.split(' ')[0]}
        </h1>
        <p style={{ color: 'var(--text-2)', fontSize: 14 }}>
          {role === 'administracion' ? 'Panel de facturación y métricas.' : '¿Con qué módulo trabajás hoy?'}
        </p>
      </div>

      {/* Tango widgets (la proyección del mes va arriba del podio; se oculta sola si no aplica) */}
      <div style={{ marginBottom: 32 }}>
        <ProyeccionWidget />
        <TangoWidget role={role} />
      </div>

      {/* Módulos */}
      {showModules && (
        <>
          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 12 }}>
            Módulos
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(170px, 1fr))', gap: 12 }}>
            {visibleModules.map(m => (
              <ModuleCard key={m.id} modulo={m} onClick={() => navigate(m.path)} />
            ))}
            {(role === 'admin' || role === 'dev') && (
              <ModuleCard
                modulo={{ id: 'admin', label: 'Administración', active: true }}
                onClick={() => navigate('/admin')}
              />
            )}
          </div>
        </>
      )}
    </div>
  );
}
